import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { NewDropdownValue, NewDropdownValueArray, UseDropdownTextProps } from '../types'

export function useDropdownText<T = NewDropdownValue | NewDropdownValueArray>({
  value,
  textConfig,
  data,
}: UseDropdownTextProps<T>) {
  const { t } = useTranslation('common')

  // Memoize header text calculation
  const headerText = useMemo(() => {
    if (value.isMultiSelect) {
      return t('newDropdown.selectedWithCount', {
        count: value.selectedOptions.length,
        prefix: undefined,
      })
    }

    return (
      data.options.find((opt) => opt.value === value.currentValue)?.triggerLabel ||
      data.options.find((opt) => opt.value === value.currentValue)?.label
    )
  }, [t, value.isMultiSelect, value.selectedOptions.length, data.options, value.currentValue])

  // Memoize trigger text calculation
  const triggerText = useMemo(() => {
    if (value.isMultiSelect) {
      const selectedCount = (value.currentValue as NewDropdownValueArray)?.length || 0
      if (selectedCount === 0) {
        return (
          textConfig.text?.triggerPlaceholder ||
          textConfig.placeholder ||
          t('newDropdown.default.triggerPlaceholderMultiple')
        )
      }

      if (selectedCount === 1) {
        const selectedOption = data.options.find(
          (opt) => opt.value === (value.currentValue as NewDropdownValueArray)[0],
        )
        return (
          selectedOption?.triggerLabel ||
          selectedOption?.label ||
          t('newDropdown.default.selectedCountSingular')
        )
      }

      if (textConfig.text?.selectedTextPrefix) {
        const prefix =
          selectedCount === 1
            ? textConfig.text.selectedTextPrefix.singular
            : textConfig.text.selectedTextPrefix.plural
        return t('newDropdown.selectedWithCount', { count: selectedCount, prefix })
      }

      return t('newDropdown.default.selectedCountPlural', { count: selectedCount })
    } else {
      if (!value.currentValue || value.currentValue === '') {
        return (
          textConfig.text?.triggerPlaceholder ||
          textConfig.placeholder ||
          t('newDropdown.default.triggerPlaceholderSingle')
        )
      }

      const selectedOption = data.options.find((opt) => opt.value === value.currentValue)
      return selectedOption?.triggerLabel || selectedOption?.label || t('newDropdown.selected')
    }
  }, [
    t,
    value.isMultiSelect,
    value.currentValue,
    textConfig.text,
    textConfig.placeholder,
    data.options,
  ])

  // Memoize display text (header text with temp mode text priority)
  const displayText = useMemo(() => {
    return textConfig.tempModeText || headerText
  }, [textConfig.tempModeText, headerText])

  // Memoize search placeholder
  const searchPlaceholder = useMemo(() => {
    return textConfig.text?.searchPlaceholder || t('newDropdown.default.searchPlaceholder')
  }, [t, textConfig.text?.searchPlaceholder])

  // Memoize empty result text
  const emptyText = useMemo(() => {
    return textConfig.text?.emptyResult || t('newDropdown.default.emptyResult')
  }, [t, textConfig.text?.emptyResult])

  return {
    triggerText,
    headerText,
    displayText,
    searchPlaceholder,
    emptyText,
  }
}
